import styled from "styled-components";
import media from "../../static/style/media";
import { useRecoilState, useRecoilValue } from "recoil";
import { accessTokenAtom, selectedDeviceNameAtom } from "../../recoil";
import { useTranslation } from "react-i18next";
import { useTimeFormatReal } from "../../library/useTimeFormat";
import { useState } from "react";
import axios from "axios"; // Axios로 API 요청
import { useQuery } from '@tanstack/react-query';
import { getCategory } from '../../apis/api/Category/getCategory';
import { MenuItem, Select as MuiSelect } from '@mui/material';


const tableTitle = [
  "name",
  "lat",
  "lon",
  "height",
  "speed",
  "quality",
  "azimuth",
  "hdop",
  "sat",
  "update",
  "set",
];

function RealTimeDeviceTable({ realtimeData }) {
  const [selectedDeviceName, setSelectedDeviceName] = useRecoilState(selectedDeviceNameAtom);
  const { t } = useTranslation();
  const { date } = useTimeFormatReal();
  const [alias, setAlias] = useState("");
  const [category, setCategory] = useState("");
  const accessToken = useRecoilValue(accessTokenAtom);
  const [editingDevice, setEditingDevice] = useState(null); // 수정 중인 장치 상태

  const tableItemOnClick = (e) => {
    const clickedDeviceName = e.currentTarget.id;
    setSelectedDeviceName((prevName) =>
      prevName === clickedDeviceName ? null : clickedDeviceName
    );
  };

  const handleAliasChange = (e) => setAlias(e.target.value);
  const handleCategoryChange = (e) => {
    const selected = e.target.value;
    console.log("선택한 카테고리:", selected);
    setCategory(selected);
  };


  const handleUpdateDevice = async (deviceSerial, port) => {
    try {
      await axios.post(
        `https://lpga.gnsson.com/tcp/device/${deviceSerial}/update`,
        {
          alias,
          category: typeof category === "string" ? category : (category?.categoryName || "Unnamed"),
          port,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      showToast("Device updated successfully!", "success");
      setEditingDevice(null);
    } catch (error) {
      console.error("Error updating device:", error);
      alert("Failed to update the device.");
      showToast("Failed to update the device.", "error");
    }
  };


  const toggleEditing = (serial, currentAlias, currentCategory) => {
    if (editingDevice === serial) {
      setEditingDevice(null);
    } else {
      setAlias(currentAlias || "Unset");
      const safeCategory = currentCategory && currentCategory !== "" ? currentCategory : "Unnamed";
      setCategory(currentCategory && currentCategory !== "" ? currentCategory : "Unnamed");
      setEditingDevice(serial);
    }
  };


  const { data: categoryDataRaw = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await getCategory(accessToken);
      return response.map((el) => el.categoryName); // categoryName만 추출
    },
  });

  // "Unnamed"을 첫 항목으로 표시하되, 선택은 불가하게 구성
const categoryData = categoryDataRaw.filter(cat => {
  if (typeof cat === 'string') return cat.toLowerCase() !== "unnamed";
  return cat.categoryName?.toLowerCase?.() !== "unnamed";
});

const categoryDataWithUnnamed = [
  ...categoryDataRaw,
  ...(categoryDataRaw.some(cat => {
    const name = typeof cat === 'string' ? cat : cat.categoryName;
    return name?.toLowerCase() === 'unnamed';
  }) ? [] : ['Unnamed'])
];



  return (
    <TableContainer>
      <Table>
        <TableHeaderWrap>
          <TableHeaderRow>
            {tableTitle.map((item, index) => (
              <TableHeader key={index}>{t(`table.${item}`)}</TableHeader>
            ))}
          </TableHeaderRow>
        </TableHeaderWrap>
        <tbody>
          {realtimeData && realtimeData.length > 0 ? (
            realtimeData.map((item, index) => {
              const quality = item.quality ? item.quality.toLowerCase().trim() : "N/A";
              const isSelected = selectedDeviceName === item.serial;
              const rowColor = quality === "off" ? "#f0f0f0" : isSelected ? "#eaf4ff" : "#fff";
              // const isPPSOLN = item.isPPSOLN;

              return (
                <TableRow
                  key={index}
                  onClick={tableItemOnClick}
                  id={item.serial}
                  style={{ backgroundColor: rowColor }}
                >
                  <TableItem>{item?.alias || " - "}</TableItem>
                  <TableItem>{item.lat?.toFixed(9) || " - "}</TableItem>
                  <TableItem>{item.lon?.toFixed(9) || " - "}</TableItem>
                  <TableItem>{(item.alt + item.geoidal)?.toFixed(3) || " - "}</TableItem>
                  <TableItem>{item.speed?.toFixed(2) || " - "}</TableItem>
                  <TableItem>{item.quality?.toUpperCase() || " - "}</TableItem>
                  <TableItem>{item.trueCourse ? item.trueCourse.replace(",T", "") : " - "}</TableItem>
                  <TableItem>{item.hdop || " - "}</TableItem>
                  <TableItem>{item.satelliteCnt || " - "}</TableItem>
                  <TableItem>{date(item.createdAt) || " - "}</TableItem>
                  {/* {isPPSOLN ? ( */}
                    {/* <TableItem>
                      <Button disabled style={{ pointerEvents: "none", opacity: "0.2" }}>None</Button>
                    </TableItem> */}
                  {/* ) : ( */}
                    <TableItem>
                      {editingDevice === item.serial ? (
                        <EditBox>
                          <Input
                            placeholder="Alias"
                            value={alias}
                            onChange={handleAliasChange}
                          />
                          <MuiSelect
                            value={category?.categoryName || category}
                            onChange={(e) => {
                              const selectedCat = categoryDataWithUnnamed.find(cat => {
                                const name = typeof cat === 'string' ? cat : cat.categoryName;
                                return name?.toLowerCase() === e.target.value.toLowerCase();
                              });
                              setCategory(selectedCat || e.target.value);
                            }}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Category Selector' }}
                          >
                            {categoryDataWithUnnamed.map((cat, i) => {
                              const label = typeof cat === 'string' ? cat : cat.categoryName;
                              const displayName = label?.toLowerCase() === 'unnamed' ? '미등록장치' : label;
                              const isUnnamed = label?.toLowerCase() === 'unnamed';
                              const isDisabled = isUnnamed && item?.isLPGA === true;

                              return (
                                <MenuItem key={i} value={label} disabled={isDisabled}>
                                  {displayName}
                                </MenuItem>
                              );
                            })}
                          </MuiSelect>

                          <ButtonGroup>
                            <Button onClick={() => handleUpdateDevice(item.serial, item.port)}>Save</Button>
                            <ButtonCancel onClick={() => setEditingDevice(null)}>Cancel</ButtonCancel>
                          </ButtonGroup>
                        </EditBox>
                      ) : (
                        <Button onClick={() => toggleEditing(item.serial, item.alias, item.category)}>Update</Button>
                      )}
                    </TableItem>
                  {/* )} */}
                </TableRow>
              );
            })
          ) : (
            <TableRow style={{ height: "300px", lineHeight: "300px", width: "100%" }}>
              <TableItem colSpan={tableTitle.length} style={{ fontSize: "20px", textAlign: 'center', color: '#777', padding: '20px', cursor: "default", pointerEvents: "none" }}>
                연결된 장치가 없습니다.
              </TableItem>
            </TableRow>
          )}
        </tbody>
      </Table>
    </TableContainer>
  );
}

// Styled Components
const EditBox = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 3px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

// Toast 알림 컴포넌트
const showToast = (message, type) => {
  const toastElement = document.createElement('div');
  toastElement.textContent = message;
  toastElement.className = type === 'success' ? 'toast-success' : 'toast-error';
  toastElement.style = `
    position: fixed;
    top: 10px;
    right: 10px;
    z-index: 9999;
    padding: 15px;
    background-color: ${type === 'success' ? '#28a745' : '#dc3545'};
    color: #fff;
    border-radius: 5px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    animation: slideDown 0.5s ease-in-out;
  `;
  document.body.appendChild(toastElement);
  setTimeout(() => {
    document.body.removeChild(toastElement);
  }, 3000);
};

const TableContainer = styled.div`
  height: 500px;
  width: 100%;
  max-width: 1300px;
  overflow-y: auto;
  margin: 0 auto;
  background-color: #fafafa;
  border: 1px solid #ddd;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 15px;
  ${media.small`
    padding: 10px;
  `}

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 5px;
    height: 100%;
  }

  @media (max-width: 576px) {
    padding: 5px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-spacing: 0;
  font-family: "Roboto", sans-serif;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }

  @media (max-width: 576px) {
    font-size: 0.75rem;
  }
`;

const TableHeaderWrap = styled.thead`
  background-color: #f1f1f1;
  top: 0;
  z-index: 1;
  border-radius: 8px 8px 0 0;
`;

const TableHeaderRow = styled.tr`
  background-color: #ececec;
  border-bottom: 2px solid #ddd;
`;

const TableHeader = styled.th`
  padding: 12px 16px;
  font-weight: 700;
  font-size: 1rem;
  color: #333;
  text-align: center;
  border-right: 1px solid #ddd;
  &:last-child {
    border-right: none;
  }
  ${media.small`
    font-size: 0.9rem;
    padding: 10px;
  `}

  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 8px;
  }

  @media (max-width: 576px) {
    font-size: 0.75rem;
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: #eaf4ff;
  }

  @media (max-width: 576px) {
    ${'' /* display: block; */}
    width: 100%;
    margin-bottom: 10px;
  }
`;

const TableItem = styled.td`
  padding: 12px 16px;
  text-align: center;
  font-size: 0.95rem;
  color: #555;
  border-bottom: 1px solid #eee;
  ${media.small`
    font-size: 0.85rem;
    padding: 10px;
  `}
  &:last-child {
    border-right: none;
  }

  @media (max-width: 768px) {
    padding: 8px;
  }

  @media (max-width: 576px) {
    ${'' /* display: block; */}
    padding: 5px;
  }
`;

const Input = styled.input`
  padding: 5px 10px;
  font-size: 0.85rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #f9f9f9;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  width: 120px;

  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
    outline: none;
  }

  &:hover {
    border-color: #007bff;
  }

  @media (max-width: 768px) {
    width: 100%;
  }

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const Button = styled.button`
  padding: 6px 10px;
  font-size: 0.8rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: #0056b3;
  }

  @media (max-width: 576px) {
    width: 100%;
    font-size: 0.7rem;
  }
`;

const ButtonCancel = styled.button`
  padding: 6px 10px;
  font-size: 0.8rem;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: #c82333;
  }

  @media (max-width: 576px) {
    width: 100%;
    font-size: 0.7rem;
  }
`;

export default RealTimeDeviceTable;
